import React from "react";
import queryString from "query-string";
import { InlineWidget } from "react-calendly";

import Footer from "../../components/Footer";
import Header from "../../components/Header";
import LocationMark from "../../components/icons/Locations/LocationMark";
import Seo from "../../components/Seo";
import TrustpilotInline from "../../components/TrustpilotInline";
import getPostcodeArea from "../../utils/postcodeArea";

import "../../../scss/main.scss";

const imagePath = "/assets/marketing-site/images";
const advisers = [
	{ file: "matthew.png", name: "Matthew" },
	{ file: "daniel-new.png", name: "Daniel" },
	{ file: "andrew.png", name: "Andrew" },
];

export default function LookingForCare({ location }) {
	const [hasHydrated, setHasHydrated] = React.useState(false);

	React.useEffect(() => {
		setHasHydrated(true);
	}, []);

	const search = queryString.parse(location?.search || "");
	const postcodeFromQuery =
		typeof search.postcode === "string" ? search.postcode.trim() : "";
	const postcode = hasHydrated ? postcodeFromQuery : "";
	const postcodeArea = getPostcodeArea(postcode);

	const hasArea = Boolean(postcodeArea);

	return (
		<div className="container edyn-redesign-shell edyn-area-result">
			<Seo
				title="Live-in Care Near You | Edyn Care"
				description="Check live-in care availability in your area and speak with an Edyn Family Care Advisor about practical next steps."
				path="/liveincare/looking-for-care/"
			/>
			<Header phoneNumber="020 3970 9902" phoneHref="tel:02039709902" />

			<main id="main-content" className="area-result">
				<section className="area-result-hero">
					<div className="site-shell area-result-hero__panel">
						<div className="area-result-hero__copy">
							<p className="eyebrow eyebrow--light">Care near you</p>
							{hasArea ? (
								<>
									<h1>
										We have carers in <em>{postcodeArea.areaName}.</em>
									</h1>
									<p className="area-result-hero__lead">
										Your postcode is covered by an Edyn care team. We can talk
										through the support needed, likely timescales and the carers
										available for your family.
									</p>
									<p className="area-result-hero__coverage">
										<span aria-hidden="true">&#10003;</span>
										{postcodeArea.district} also covers{" "}
										{postcodeArea.areaListLabel}
									</p>
								</>
							) : (
								<>
									<h1>Let us check care in your area.</h1>
									<p className="area-result-hero__lead">
										We could not match{" "}
										{postcode ? `“${postcode}”` : "that postcode"} to our
										location guide. Try the full postcode again or speak with a
										Care Advisor and we will check it for you.
									</p>
								</>
							)}
							<div className="button-row">
								<a className="button button--cream" href="tel:02039709902">
									Speak with a Care Advisor Now
								</a>
								<a className="button button--outline-light" href="#next-step">
									Book a call
								</a>
							</div>
						</div>

						<aside className="area-result-hero__summary">
							<div className="area-result-hero__pin" aria-hidden="true">
								<LocationMark />
							</div>
							<p>{hasArea ? "Postcode checked" : "Check your postcode"}</p>
							<strong>
								{hasArea ? postcodeArea.postcode : postcode || "Your area"}
							</strong>
							<span>
								{hasArea
									? `Local care support around ${postcodeArea.areaName}`
									: "We can check availability with you"}
							</span>
							<div
								className="area-result-hero__advisers"
								role="img"
								aria-label="Edyn Family Care Advisors"
							>
								{advisers.map((adviser) => (
									<img
										alt=""
										key={adviser.file}
										loading="eager"
										src={`${imagePath}/team/${adviser.file}`}
									/>
								))}
							</div>
							<p className="area-result-hero__advisor-label">
								A friendly team ready to listen
							</p>
							<TrustpilotInline />
						</aside>
					</div>
				</section>

				<section
					className="area-result-confirmation"
					aria-label="Your care options"
					hidden={!hasArea}
				>
					<div className="site-shell area-result-confirmation__grid">
						<article>
							<span>01</span>
							<strong>{hasArea ? postcodeArea.areaName : "Your area"}</strong>
							<p>Your local area</p>
						</article>
						<article>
							<span>02</span>
							<strong>Fully managed</strong>
							<p>One Edyn team around the care</p>
						</article>
						<article>
							<span>03</span>
							<strong>Personal to you</strong>
							<p>Care planned around real life</p>
						</article>
					</div>
				</section>

				<section key="area-next" className="area-result-next section-space">
					<div className="site-shell">
						<div className="section-heading section-heading--split">
							<div>
								<p className="eyebrow">A clear next step</p>
								<h2>Start with a conversation, not a commitment.</h2>
							</div>
							<p>
								A Family Care Advisor will listen to what is changing at home
								and help you understand whether live-in care is the right fit.
							</p>
						</div>
						<ol className="area-result-next__steps">
							<li>
								<span>01</span>
								<h3>Tell us what is happening</h3>
								<p>Share the routines, needs and concerns that matter now.</p>
							</li>
							<li>
								<span>02</span>
								<h3>Understand your options</h3>
								<p>
									We explain likely timescales, costs and how care is managed.
								</p>
							</li>
							<li>
								<span>03</span>
								<h3>Meet carefully selected carers</h3>
								<p>Review suitable professional carers before care begins.</p>
							</li>
						</ol>
					</div>
				</section>

				<section
					key="area-calendly"
					className="area-result-calendly section-space"
					id="next-step"
				>
					<div className="site-shell">
						<div className="section-heading section-heading--split">
							<div>
								<p className="eyebrow">Book a care consultation</p>
								<h2>Choose a time to talk.</h2>
							</div>
							<p>
								Choose a convenient time for a conversation with a Care Advisor.
								There is no obligation and you do not need to have everything
								worked out.
							</p>
						</div>
						<div className="area-result-calendly__embed">
							<InlineWidget
								url="https://calendly.com/liveincare/care-consultation?hide_gdpr_banner=1"
								styles={{ minWidth: "320px", height: "700px" }}
							/>
						</div>
					</div>
				</section>
			</main>

			<Footer
				includeCTA={false}
				phoneNumber="020 3970 9902"
				phoneHref="tel:02039709902"
			/>
		</div>
	);
}
